import { useEffect, useRef, useState } from 'react';
import { fetchVfsLinkContent } from './chat-files';
import { effectiveContent } from './file-record';
import { queryClient, queryKeys } from './query-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import type { WorkspaceFile } from './workspace-files';

/** An agent turn writes in bursts (create, then a few updates), and one re-read covers the whole burst. */
const LIVE_REFRESH_DEBOUNCE_MS = 400;

/** `spaces/<slug>/…` (the agent's spelling) and `workspaces/<slug>/…` (the attachment's) name one file. */
const samePathKey = (path: string): string => path.trim().replace(/^spaces\//, 'workspaces/');

// The second trigger, beside the file-system signal: the CHAT saying a message just carried this file.
// When the agent rewrites a file and re-attaches it, the new attachment IS the news — and it arrives on the
// chat's own channel, so the open copy refreshes even when the file-system push is late, dropped, or was
// never connected. `ChatSurface` calls `nudgeLiveDocs` with the paths of attachments it has not seen yet.
const nudgeListeners = new Set<(paths: ReadonlySet<string>) => void>();

export function nudgeLiveDocs(paths: Iterable<string>): void {
  const keys = new Set([...paths].map(samePathKey));
  if (keys.size === 0) return;
  for (const listener of [...nudgeListeners]) listener(keys);
}

/**
 * The open document, kept LIVE: while the viewer is up, every file-system change re-reads the file at
 * `vfsPath` and swaps the new body in. This is how a report the agent is fixing updates in front of the
 * reader without them reopening it. Spec: `docs/superpowers/specs/2026-10-09-chat-file-pane-design.md`.
 *
 * - The signal is the shared `individuals-file-system-changed` channel. It carries no path, so every
 *   change re-reads this one file; `MeetingNotesCard` uses the same pattern for its own file.
 * - The re-read is the path a workspace-path link opens through (`fetchVfsLinkContent`: the Space's list,
 *   then the scoped single-file read). Both caches are dropped first, so it reads the server and not the
 *   copy the viewer was opened from.
 * - `paused` while the editor is open. A refresh under the editor would replace the body it is editing,
 *   and the editor's own Save is the writer there. Changes made meanwhile are picked up by the next signal.
 * - A failed or empty re-read keeps the copy on screen. A reader mid-document never sees it turn into an
 *   error because one read failed.
 *
 * No `vfsPath` (a plain-URL attachment, an upload) means nothing to re-read: the file is returned as given.
 */
export function useLiveDocFile(file: WorkspaceFile, vfsPath: string | undefined, paused: boolean): WorkspaceFile {
  const [live, setLive] = useState(file);
  const liveRef = useRef(live);
  liveRef.current = live;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generation = useRef(0);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      // Any read still in flight answers into a viewer that is gone.
      generation.current += 1;
    },
    [],
  );

  const reread = async (path: string) => {
    const mine = ++generation.current;
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFile(path) }),
      // Prefix match: every Space's list, since the path's Space id is only known after resolving it.
      queryClient.invalidateQueries({ queryKey: ['workspace-files'] }),
    ]);
    const content = await fetchVfsLinkContent(path);
    if (mine !== generation.current || pausedRef.current || !content) return;
    if (content === effectiveContent(liveRef.current)) return;
    // `content` now carries the body (or its reference header), so the reference fields of the copy we
    // opened would only describe the old bytes.
    setLive({ ...liveRef.current, content, referenceUrl: undefined });
  };

  const schedule = () => {
    if (!vfsPath || pausedRef.current) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      void reread(vfsPath);
    }, LIVE_REFRESH_DEBOUNCE_MS);
  };

  useFileSystemDuplexer(schedule);

  useEffect(() => {
    if (!vfsPath) return undefined;
    const mine = samePathKey(vfsPath);
    const listener = (paths: ReadonlySet<string>) => {
      if (paths.has(mine)) schedule();
    };
    nudgeListeners.add(listener);
    return () => {
      nudgeListeners.delete(listener);
    };
    // `schedule` reads refs only (see below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vfsPath]);

  // Leaving the editor re-reads once: a signal that arrived while it was open was skipped, and the
  // editor's own Save is itself a write the reader now expects to see.
  const wasPaused = useRef(paused);
  useEffect(() => {
    if (wasPaused.current && !paused) schedule();
    wasPaused.current = paused;
    // `schedule` reads refs only; re-running on its identity would re-read on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  return live;
}
